<template>
    <div class="page-container">
        <div v-if="loading" class="loader-container">
            <div class="loader"></div>
        </div>

        <!-- Top Bar -->
        <div class="top-bar">
            <div class="top-bar-left">
                <h2 class="page-title">
                    <i class="tim-icons icon-bank"></i>
                    Klijenti
                </h2>
                <span class="count-badge">{{ clients.length }}</span>
            </div>
        </div>

        <!-- Summary -->
        <div class="summary-bar">
            <div class="summary-item">
                <span class="summary-label">{{ $t('admin.dashboard.activeClients') }}:</span>
                <span class="summary-value text-green">{{ clients.filter(c => c.subscriptionStatus === 'ACTIVE').length }}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">Trial:</span>
                <span class="summary-value text-orange">{{ clients.filter(c => c.subscriptionStatus === 'TRIAL').length }}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">{{ $t('admin.dashboard.totalEsirs') }}:</span>
                <span class="summary-value">{{ clients.reduce((s, c) => s + (c.esirCount || 0), 0) }}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">{{ $t('admin.clients.monthlyRevenue') }}:</span>
                <span class="summary-value text-blue">{{ formatNumber(clients.reduce((s, c) => s + (c.monthlyTotal || 0), 0)) }} RSD</span>
            </div>
        </div>

        <!-- Table -->
        <div class="table-section">
            <div class="table-toolbar">
                <base-input v-model="search" :placeholder="$t('common.search') + '...'" class="search-input">
                </base-input>
            </div>

            <div class="table-container">
                <table class="custom-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{{ $t('common.name') }}</th>
                            <th>{{ $t('login.tin') }}</th>
                            <th>{{ $t('common.status') }}</th>
                            <th>{{ $t('nav.esirs') }}</th>
                            <th>{{ $t('admin.clients.pricePerEsir') }}</th>
                            <th>{{ $t('admin.clients.monthlyRevenue') }}</th>
                            <th>Trial</th>
                            <th>{{ $t('common.date') }}</th>
                            <th>{{ $t('common.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(c, index) in filteredClients" :key="c.id">
                            <td>{{ index + 1 }}</td>
                            <td class="name-cell">{{ c.name }}</td>
                            <td><span class="mono">{{ c.tin || '-' }}</span></td>
                            <td>
                                <span :class="['status-tag', 'status-' + c.subscriptionStatus.toLowerCase()]">
                                    {{ statusLabel(c.subscriptionStatus) }}
                                </span>
                            </td>
                            <td>{{ c.esirCount }}</td>
                            <td><span class="mono">{{ formatNumber(c.pricePerEsir) }}</span></td>
                            <td><span class="mono">{{ formatNumber(c.monthlyTotal) }}</span></td>
                            <td>
                                <span v-if="c.subscriptionStatus === 'TRIAL'" :class="{'text-danger': c.trialDaysRemaining <= 3}">
                                    {{ c.trialDaysRemaining }} dana
                                </span>
                                <span v-else class="no-data">-</span>
                            </td>
                            <td class="small-text">{{ formatDate(c.timeStamp) }}</td>
                            <td>
                                <div class="action-buttons-cell">
                                    <base-button type="info" icon size="sm" class="btn-link"
                                        @click="openSubscription(c)" :title="$t('admin.clients.subscription')">
                                        <i class="tim-icons icon-settings-gear-63"></i>
                                    </base-button>
                                    <base-button type="success" icon size="sm" class="btn-link"
                                        @click="openPayment(c)" :title="$t('admin.payments.newPayment')">
                                        <i class="tim-icons icon-coins"></i>
                                    </base-button>
                                    <base-button type="default" icon size="sm" class="btn-link"
                                        @click="viewPayments(c)" :title="$t('admin.clients.paymentHistory')">
                                        <i class="tim-icons icon-paper"></i>
                                    </base-button>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="filteredClients.length === 0">
                            <td colspan="10" class="empty-row">
                                <i class="tim-icons icon-zoom-split"></i>
                                <p>{{ search ? 'Nema rezultata pretrage' : 'Nema klijenata' }}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Subscription Modal -->
        <transition name="modal-fade">
            <div v-if="showSubModal" class="modal-overlay" @click="showSubModal = false">
                <div class="modal-container" @click.stop>
                    <div class="modal-header">
                        <h3>
                            <i class="tim-icons icon-settings-gear-63"></i>
                            {{ $t('admin.clients.subscription') }}: {{ selectedClient ? selectedClient.name : '' }}
                        </h3>
                        <button @click="showSubModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="client-info-bar">
                            <span>{{ $t('nav.esirs') }}: <strong>{{ selectedClient ? selectedClient.esirCount : 0 }}</strong></span>
                            <span>{{ $t('nav.locations') }}: <strong>{{ selectedClient ? selectedClient.locationCount : 0 }}</strong></span>
                            <span>{{ $t('nav.users') }}: <strong>{{ selectedClient ? selectedClient.userCount : 0 }}</strong></span>
                        </div>
                        <div class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ $t('admin.clients.pricePerEsir') }} (RSD)</label>
                                <base-input v-model="subForm.pricePerEsir" type="number" placeholder="3500.00"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">Trial dana</label>
                                <base-input v-model="subForm.trialDays" type="number" placeholder="15"></base-input>
                            </div>
                            <div class="form-group full-width">
                                <label class="form-label">{{ $t('admin.clients.subscription') }}</label>
                                <select v-model="subForm.subscriptionStatus" class="form-select">
                                    <option value="TRIAL">TRIAL</option>
                                    <option value="ACTIVE">ACTIVE</option>
                                    <option value="SUSPENDED">SUSPENDED</option>
                                    <option value="EXPIRED">EXPIRED</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <base-button type="primary" @click="saveSubscription()">
                            <i class="tim-icons icon-check-2"></i> {{ $t('common.save') }}
                        </base-button>
                        <base-button type="default" @click="showSubModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Quick Payment Modal -->
        <transition name="modal-fade">
            <div v-if="showPayModal" class="modal-overlay" @click="showPayModal = false">
                <div class="modal-container" @click.stop>
                    <div class="modal-header" style="background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);">
                        <h3>
                            <i class="tim-icons icon-coins"></i>
                            {{ $t('admin.payments.newPayment') }}: {{ selectedClient ? selectedClient.name : '' }}
                        </h3>
                        <button @click="showPayModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="auto-calc">
                            <span>{{ selectedClient ? selectedClient.esirCount : 0 }} ESIR-ova × {{ formatNumber(selectedClient ? selectedClient.pricePerEsir : 0) }} RSD =
                                <strong>{{ formatNumber(autoAmount) }} RSD</strong>
                            </span>
                        </div>
                        <div class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ $t('admin.payments.amount') }} (RSD)</label>
                                <base-input v-model="payForm.amount" type="number" :placeholder="String(autoAmount)"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('admin.payments.periodFrom') }}</label>
                                <base-input v-model="payForm.periodFrom" type="date"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('admin.payments.periodTo') }}</label>
                                <base-input v-model="payForm.periodTo" type="date"></base-input>
                            </div>
                            <div class="form-group full-width">
                                <label class="form-label">{{ $t('admin.payments.note') }}</label>
                                <textarea v-model="payForm.note" class="form-textarea" rows="2" :placeholder="$t('admin.payments.note')"></textarea>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <base-button type="success" @click="savePayment()">
                            <i class="tim-icons icon-check-2"></i> Evidentiraj uplatu
                        </base-button>
                        <base-button type="default" @click="showPayModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Payments History Modal -->
        <transition name="modal-fade">
            <div v-if="showPayHistoryModal" class="modal-overlay" @click="showPayHistoryModal = false">
                <div class="modal-container modal-lg" @click.stop>
                    <div class="modal-header">
                        <h3>
                            <i class="tim-icons icon-paper"></i>
                            Uplate: {{ selectedClient ? selectedClient.name : '' }}
                        </h3>
                        <button @click="showPayHistoryModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body" style="padding: 0;">
                        <table class="custom-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('common.date') }}</th>
                                    <th>{{ $t('common.amount') }}</th>
                                    <th>{{ $t('nav.esirs') }}</th>
                                    <th>{{ $t('admin.clients.pricePerEsir') }}</th>
                                    <th>{{ $t('admin.payments.periodFrom') }}</th>
                                    <th>{{ $t('admin.payments.note') }}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="p in clientPayments" :key="p.id">
                                    <td class="small-text">{{ formatDate(p.timeStamp) }}</td>
                                    <td><span class="mono">{{ formatNumber(p.amount) }}</span></td>
                                    <td>{{ p.esirCount }}</td>
                                    <td><span class="mono">{{ formatNumber(p.pricePerEsir) }}</span></td>
                                    <td class="small-text">
                                        <span v-if="p.periodFrom">{{ p.periodFrom }} - {{ p.periodTo }}</span>
                                        <span v-else class="no-data">-</span>
                                    </td>
                                    <td class="small-text">{{ p.note || '-' }}</td>
                                    <td>
                                        <base-button type="danger" icon size="sm" class="btn-link"
                                            @click="deletePayment(p)" :title="$t('common.delete')">
                                            <i class="tim-icons icon-trash-simple"></i>
                                        </base-button>
                                    </td>
                                </tr>
                                <tr v-if="clientPayments.length === 0">
                                    <td colspan="7" class="empty-row"><p>{{ $t('common.noData') }}</p></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="modal-footer">
                        <base-button type="default" @click="showPayHistoryModal = false">
                            Zatvori
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="notif-fade">
            <div v-if="toast.message" :class="['toast-notif', toast.type]" @click="toast.message = ''">
                <i :class="toast.type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                <span>{{ toast.message }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'

export default {
    name: 'AdminClients',
    data() {
        return {
            loading: false,
            clients: [],
            search: '',
            selectedClient: null,
            showSubModal: false,
            showPayModal: false,
            showPayHistoryModal: false,
            subForm: {},
            payForm: {},
            clientPayments: [],
            toast: { message: '', type: 'success' }
        }
    },
    computed: {
        filteredClients() {
            let list = this.clients.filter(c => c.id !== 'SYSTEM')
            if (!this.search) return list
            const s = this.search.toLowerCase()
            return list.filter(c =>
                (c.name && c.name.toLowerCase().includes(s)) ||
                (c.tin && c.tin.toLowerCase().includes(s))
            )
        },
        autoAmount() {
            if (!this.selectedClient) return 0
            return (this.selectedClient.esirCount || 0) * (this.selectedClient.pricePerEsir || 0)
        }
    },
    created() {
        this.getClients()
    },
    methods: {
        getClients() {
            this.loading = true
            axios.get('/api/v1/admin/clients').then(res => {
                this.loading = false
                this.clients = res.data
            }).catch(err => {
                this.loading = false
                this.showToast(this.$t('pos.result.error'), 'error')
            })
        },

        // Subscription
        openSubscription(client) {
            this.selectedClient = client
            this.subForm = {
                pricePerEsir: String(client.pricePerEsir),
                trialDays: String(client.trialDays),
                subscriptionStatus: client.subscriptionStatus
            }
            this.showSubModal = true
        },

        saveSubscription() {
            this.loading = true
            axios.put('/api/v1/admin/client/' + this.selectedClient.id + '/subscription', this.subForm).then(res => {
                this.loading = false
                this.showSubModal = false
                this.showToast('Subscription updated', 'success')
                this.getClients()
            }).catch(err => {
                this.loading = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        // Payment
        openPayment(client) {
            this.selectedClient = client
            const today = new Date()
            const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())
            this.payForm = {
                amount: '',
                periodFrom: this.dateToStr(today),
                periodTo: this.dateToStr(nextMonth),
                note: ''
            }
            this.showPayModal = true
        },

        savePayment() {
            this.loading = true
            const payload = {
                clientId: this.selectedClient.id,
                amount: this.payForm.amount || null,
                periodFrom: this.payForm.periodFrom || null,
                periodTo: this.payForm.periodTo || null,
                note: this.payForm.note || null
            }
            axios.post('/api/v1/admin/payment', payload).then(res => {
                this.loading = false
                this.showPayModal = false
                this.showToast(this.$t('common.save'), 'success')
                this.getClients()
            }).catch(err => {
                this.loading = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        // Payment History
        viewPayments(client) {
            this.selectedClient = client
            this.clientPayments = []
            this.showPayHistoryModal = true
            axios.get('/api/v1/admin/payments/client/' + client.id).then(res => {
                this.clientPayments = res.data
            })
        },

        deletePayment(payment) {
            if (!confirm(this.$t('common.delete') + '?')) return
            axios.delete('/api/v1/admin/payment/' + payment.id).then(res => {
                this.showToast(this.$t('common.delete'), 'success')
                // Refresh
                this.viewPayments(this.selectedClient)
                this.getClients()
            }).catch(err => {
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        statusLabel(s) {
            const map = { TRIAL: 'Trial', ACTIVE: 'Aktivan', SUSPENDED: 'Suspendovan', EXPIRED: 'Istekao' }
            return map[s] || s
        },

        formatNumber(val) {
            if (!val && val !== 0) return '0.00'
            return Number(val).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        formatDate(d) {
            if (!d) return '-'
            return new Date(d).toLocaleDateString('sr-RS')
        },

        dateToStr(d) {
            return d.toISOString().split('T')[0]
        },

        showToast(message, type) {
            this.toast = { message, type }
            setTimeout(() => { this.toast = { message: '', type: 'success' } }, 3000)
        }
    }
}
</script>

<style scoped>
.page-container { padding: 0; }
.loader-container {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.7); display: flex;
    align-items: center; justify-content: center; z-index: 9999;
}
.loader {
    width: 40px; height: 40px;
    border: 4px solid #e9ecef; border-top-color: #1a73e8;
    border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.top-bar {
    display: flex; justify-content: space-between;
    align-items: center; margin-bottom: 15px; flex-wrap: wrap; gap: 10px;
}
.top-bar-left { display: flex; align-items: center; gap: 10px; }
.page-title {
    margin: 0; font-size: 22px; font-weight: 700;
    color: #2c3e50; display: flex; align-items: center; gap: 8px;
}
.page-title i { color: #1a73e8; }
.count-badge {
    background: #1a73e8; color: white;
    padding: 2px 10px; border-radius: 12px;
    font-size: 13px; font-weight: 600;
}

/* Summary */
.summary-bar {
    display: flex; gap: 15px; margin-bottom: 15px; flex-wrap: wrap;
}
.summary-item {
    background: #ffffff; border: 1px solid #e0e0e0;
    border-radius: 10px; padding: 10px 18px;
    display: flex; align-items: center; gap: 8px;
}
.summary-label { font-size: 13px; color: #6c757d; }
.summary-value { font-size: 16px; font-weight: 700; color: #2c3e50; }
.text-green { color: #28a745 !important; }
.text-orange { color: #fd7e14 !important; }
.text-blue { color: #1a73e8 !important; }
.text-danger { color: #dc3545 !important; font-weight: 700; }

/* Table */
.table-section {
    background: #ffffff; border-radius: 12px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden;
}
.table-toolbar { padding: 15px; border-bottom: 1px solid #e9ecef; }
.search-input { max-width: 350px; margin-bottom: 0; }
.table-container { overflow-x: auto; }
.custom-table { width: 100%; border-collapse: collapse; }
.custom-table th, .custom-table td {
    padding: 10px 12px; text-align: left;
    border-bottom: 1px solid #e9ecef;
    color: #2c3e50; font-size: 13px;
}
.custom-table th {
    background: #f8f9fa; font-weight: 600;
    font-size: 11px; text-transform: uppercase;
    letter-spacing: 0.3px; color: #495057;
}
.custom-table tbody tr:hover { background: #f1f5ff; }
.name-cell { font-weight: 600; }
.small-text { font-size: 12px; color: #6c757d; }
.mono {
    font-family: monospace; background: #f1f3f5;
    padding: 2px 6px; border-radius: 4px; font-size: 12px;
}
.status-tag {
    padding: 3px 10px; border-radius: 10px;
    font-size: 11px; font-weight: 600; text-transform: uppercase;
}
.status-trial { background: #fff3cd; color: #856404; }
.status-active { background: #d4edda; color: #155724; }
.status-suspended { background: #f8d7da; color: #721c24; }
.status-expired { background: #e2e3e5; color: #383d41; }
.no-data { color: #adb5bd; }
.action-buttons-cell { display: flex; gap: 4px; }
.empty-row { text-align: center; padding: 30px !important; color: #6c757d; }
.empty-row i { font-size: 30px; display: block; margin-bottom: 8px; opacity: 0.4; }

/* Auto calc */
.auto-calc {
    background: #e7f3ff; padding: 12px 16px; border-radius: 8px;
    margin-bottom: 18px; font-size: 14px; color: #1a73e8; text-align: center;
}
.auto-calc strong { font-size: 16px; }

/* Client info bar */
.client-info-bar {
    display: flex; gap: 20px; margin-bottom: 18px;
    padding: 10px 15px; background: #f8f9fa; border-radius: 8px;
    font-size: 13px; color: #495057;
}
.client-info-bar strong { color: #2c3e50; }

/* Modals */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); display: flex;
    align-items: center; justify-content: center;
    z-index: 99999; backdrop-filter: blur(3px);
}
.modal-container {
    background: #ffffff; border-radius: 14px;
    max-width: 650px; width: 95%; max-height: 90vh;
    overflow: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}
.modal-lg { max-width: 850px; }
.modal-header {
    background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
    padding: 18px 20px; display: flex;
    justify-content: space-between; align-items: center;
    border-radius: 14px 14px 0 0;
}
.modal-header h3 {
    margin: 0; color: white; font-size: 17px;
    font-weight: 600; display: flex; align-items: center; gap: 8px;
}
.modal-close {
    background: none; border: none; color: white;
    font-size: 26px; cursor: pointer;
    width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; transition: background 0.2s;
}
.modal-close:hover { background: rgba(255,255,255,0.2); }
.modal-body { padding: 20px; color: #2c3e50; }
.modal-footer {
    padding: 14px 20px; background: #f8f9fa;
    border-top: 1px solid #e9ecef;
    display: flex; gap: 10px; justify-content: flex-end;
}
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.form-group { margin-bottom: 0; }
.full-width { grid-column: 1 / -1; }
.form-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #495057; margin-bottom: 5px;
}
.form-select {
    width: 100%; padding: 10px 12px;
    border: 2px solid #dee2e6; border-radius: 8px;
    background: white; font-size: 14px; color: #2c3e50;
    cursor: pointer;
}
.form-select:focus { outline: none; border-color: #1a73e8; }
.form-textarea {
    width: 100%; padding: 10px 12px;
    border: 2px solid #dee2e6; border-radius: 8px;
    font-size: 14px; color: #2c3e50; resize: vertical;
    font-family: inherit;
}
.form-textarea:focus { outline: none; border-color: #1a73e8; }

.toast-notif {
    position: fixed; top: 25px; right: 25px;
    padding: 12px 20px; border-radius: 10px;
    display: flex; align-items: center; gap: 8px;
    font-size: 14px; font-weight: 500;
    z-index: 100000; cursor: pointer;
    box-shadow: 0 4px 15px rgba(0,0,0,0.15); color: white;
}
.toast-notif.success { background: #28a745; }
.toast-notif.error { background: #dc3545; }
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.3s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(50px); }

@media (max-width: 768px) {
    .form-grid { grid-template-columns: 1fr; }
    .summary-bar { flex-direction: column; }
    .top-bar { flex-direction: column; align-items: flex-start; }
}
</style>